import React, { useState, useEffect } from "react";
import axios from "axios";
import { Heart, Users, GraduationCap, Clock, Mail, Loader2 } from "lucide-react";
import { PageHeader, Section, Card, Prose, ButtonLink } from "../components/ui-kit";
import { useContact } from "../context/SiteSettingsContext";
import { CareersApplyForm } from "../components/CareersApplyForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { icon: Heart, title: "Meaningful work", text: "Help young adults build confidence, skills and connection every day." },
  { icon: Users, title: "Supportive team", text: "Work alongside a friendly, values-led team that looks out for one another." },
  { icon: GraduationCap, title: "Learn and grow", text: "We value learning and support people to develop in their roles." },
  { icon: Clock, title: "Weekday hours", text: "Our service runs Monday to Friday, supporting a healthy work-life balance." },
];

export default function Careers() {
  const CONTACT = useContact();
  const [recruiting, setRecruiting] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/settings/recruitment`)
      .then((r) => setRecruiting(!!r.data.recruiting))
      .catch(() => setRecruiting(false));
  }, []);

  const open = recruiting === true;

  return (
    <>
      <PageHeader
        eyebrow="Careers"
        title="Join a team that makes a difference"
        intro="If you are warm, patient and believe in the potential of every person, we would love to hear from you. Working at Spire Inclusion is genuinely rewarding."
        testid="careers-header"
      />

      <div className="bg-stone-100 border-b border-stone-200" data-testid="recruitment-status">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col gap-3 rounded-2xl border-2 border-stone-300 bg-white p-5 sm:flex-row sm:items-center">
            {recruiting === null ? (
              <span className="inline-flex items-center gap-2 text-lg text-stone-600" data-testid="recruitment-status-loading">
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                Checking current opportunities…
              </span>
            ) : (
              <>
                <span
                  className={`inline-flex items-center gap-2 self-start rounded-full px-4 py-1.5 text-base font-semibold ${
                    open ? "bg-green-100 text-green-800" : "bg-stone-200 text-stone-800"
                  }`}
                  data-testid="recruitment-status-badge"
                >
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${open ? "bg-green-600" : "bg-red-500"}`}
                    aria-hidden="true"
                  />
                  Recruitment: {open ? "Open" : "Closed"}
                </span>
                <p className="text-lg text-stone-700">
                  {open
                    ? "We currently have opportunities available. We'd love to hear from you — get in touch below."
                    : "We are not actively recruiting at the moment. You are still welcome to register your interest, and we'll keep you in mind for future opportunities."}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <Section tone="default">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {REASONS.map((r) => (
            <Card key={r.title} data-testid={`careers-reason-${r.title.split(" ")[0].toLowerCase()}`}>
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-50 text-orange-700">
                <r.icon className="h-6 w-6" aria-hidden="true" />
              </span>
              <h2 className="mt-5 font-heading text-xl font-semibold text-stone-900">{r.title}</h2>
              <p className="mt-2 text-lg text-stone-700 leading-relaxed">{r.text}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section tone="white">
        <div className="grid items-start gap-12 lg:grid-cols-2">
          <Prose>
            <h2 className="font-heading text-3xl font-semibold text-stone-900">Who we look for</h2>
            <p>
              We welcome people from all backgrounds who share our values of respect, independence
              and belonging. What matters most is a caring, can-do attitude and a genuine interest
              in supporting young adults with disabilities.
            </p>
            <p>
              All roles include the right training and support so you can do your best work.
            </p>
          </Prose>
          <Card>
            {open ? (
              <>
                <h2 className="font-heading text-2xl font-semibold text-stone-900">Apply now</h2>
                <p className="mt-3 text-lg text-stone-700 leading-relaxed">
                  Interested in joining us? Fill in this short form and we'll get back to you.
                  No CV needed at this stage — just tell us a bit about yourself.
                </p>
                <div className="mt-6">
                  <CareersApplyForm />
                </div>
              </>
            ) : (
              <>
                <h2 className="font-heading text-2xl font-semibold text-stone-900">Register your interest</h2>
                <p className="mt-3 text-lg text-stone-700 leading-relaxed">
                  We are not recruiting right now, but we'd still love to hear from you. Send us a
                  message to register your interest and we'll keep your details on file for when
                  roles become available.
                </p>
                <p className="mt-4 flex items-center gap-3 text-lg font-medium text-stone-900">
                  <Mail className="h-5 w-5 text-orange-700" aria-hidden="true" />
                  {CONTACT.email}
                </p>
                <ButtonLink to="/contact" className="mt-6" data-testid="careers-contact-button">
                  Register your interest
                </ButtonLink>
              </>
            )}
          </Card>
        </div>
      </Section>
    </>
  );
}
